/**
 * Parser-blocking inline scripts for the first paint.
 *
 * They run while the browser parses the HTML — before React, before any
 * chunk — so the first frame already knows two things from local storage:
 *   1. the theme (handled by getThemeBootInlineScript in theme.js), and
 *   2. whether this browser holds a Convex Auth session.
 *
 * (2) is only a hint. It decides what to DRAW (app frame vs sign-in plate),
 * never what to grant. StudioAuthGate still makes the real call and dismisses
 * the frame for sign-in if the stored session turns out to be bad.
 *
 * Do not move these into next/script `beforeInteractive`: in the App Router
 * those are queued on `self.__next_s` and only run once the Next runtime
 * boots, i.e. after first paint. That delay is exactly what produced the
 * dark-then-light blank flashes.
 */

export const BOOT_FRAME_ID = "ys-boot-frame";

/** Same rule as studio-session-hint.ts: this deployment's Convex Auth JWT key. */
const TOKEN_PREFIX = "__convexAuthJWT";

/**
 * `__convexAuthJWT_<deployment URL, non-alphanumerics stripped>`, exactly as
 * ConvexAuthProvider namespaces it. Only this key counts: a stale token under
 * another namespace must not paint the signed-in frame after sign-out. Empty
 * when the URL is unknown, in which case the script falls back to a prefix scan.
 */
function sessionTokenKey(): string {
  const url = process.env.NEXT_PUBLIC_CONVEX_URL ?? "";
  return url ? `${TOKEN_PREFIX}_${url.replace(/[^a-zA-Z0-9]/g, "")}` : "";
}

/** Inline helper for theme boot: true when this browser holds a Convex Auth JWT. */
export function getConvexSessionProbeInlineFragment(): string {
  return `function __ysHasConvexSession(){try{var ls=window.localStorage,tk=${JSON.stringify(sessionTokenKey())},ok=function(v){return !!(v&&v.replace(/\\s/g,""))};if(tk)return ok(ls.getItem(tk));for(var i=0;i<ls.length;i++){var k=ls.key(i);if(k&&k.indexOf(${JSON.stringify(TOKEN_PREFIX)})===0&&ok(ls.getItem(k)))return true}return false}catch(e){return false}}`;
}

/** Mirrors studioPanelSizes.js so the frame's rail matches the saved split. */
const PANEL_SIZES_KEY = "yatishara-studio-main-panel-sizes";

/**
 * Head script (runs after the theme script, so data-appearance is set). Sets
 * `data-ys-boot` on <html> to
 *   "app"  — app route with a stored session → show the app frame
 *   "auth" — app route, no session → sign-in plate colour, no frame
 *   "page" — any other route (legal, meet, pay, oauth, …) and every page on the community host (its "/" is the community sign-up, not the app) → untouched
 * For "app" it also preloads the frame's logo for the active theme and copies
 * the saved rail split into --ys-boot-rail.
 */
export function getBootDecisionInlineScript(): string {
  return `(function(){try{var d=document.documentElement;var p=location.pathname||"/";var app=location.hostname.indexOf("community.")!==0&&(p==="/"||/^\\/u\\/[^\\/]+\\/?$/.test(p));var has=false;if(app){try{var ls=window.localStorage,tk=${JSON.stringify(sessionTokenKey())},ok=function(v){return !!(v&&v.replace(/\\s/g,""))};if(tk){has=ok(ls.getItem(tk))}else{for(var i=0;i<ls.length;i++){var k=ls.key(i);if(k&&k.indexOf(${JSON.stringify(TOKEN_PREFIX)})===0&&ok(ls.getItem(k))){has=true;break}}}}catch(e){}}var mode=app?(has?"app":"auth"):"page";window.__ysBoot=mode;d.setAttribute("data-ys-boot",mode);if(mode==="app"){try{var lk=document.createElement("link");lk.rel="preload";lk.as="image";lk.href=d.getAttribute("data-appearance")==="dark"?"/branding/sanity-v2-logo-light-96.webp":"/branding/sanity-v2-logo-dark-96.webp";document.head.appendChild(lk)}catch(e){}try{var s=JSON.parse(localStorage.getItem(${JSON.stringify(PANEL_SIZES_KEY)})||"null");var r=s&&Number(s[0]);if(r>=16&&r<=42)d.style.setProperty("--ys-boot-rail",r+"%")}catch(e){}try{if(localStorage.getItem("studio-dm-peer-sidebar-open")==="0")d.setAttribute("data-ys-boot-peer","0")}catch(e){}}}catch(e){}})();`;
}

/** Mirrors STUDIO_OPEN_TABS_BASE (studio-account-storage.ts): `${base}:${userId}`. */
const OPEN_TABS_BASE = "yatishara-studio-open-tabs-v1";

/**
 * Titles for tab keys that carry no saved title, mirroring studioEntries.js.
 * Anything else falls back to the saved snapshot title/name, or a label bar.
 */
const STATIC_TAB_TITLES: Record<string, string> = {
  "messages:": "DMs",
  "mail:": "Mail",
  "files:": "Files",
  "filepick:": "Choose files",
  "live:": "Record",
  "academy:": "Academy",
  "organization:": "Organization",
  "connectors:": "Connectors",
  "workflows:": "Workflows",
};

const ADMIN_TAB_TITLES: Record<string, string> = {
  payments: "Payments",
  customers: "Customers",
  sellers: "Sellers",
  marketplace: "Sellers",
  offers: "Offers",
  jobs: "Jobs",
  assets: "Assets",
  academy: "Academy",
  agents: "Agents",
  payouts: "Payouts",
  tools: "Tools",
  setup: "Tools",
  pricing: "Tools",
};

/**
 * Header greeting + quote for the skeleton (boot frame and sign-in page), in
 * the voice of the real empty tab strip (UnifiedTabStrip tabEmptyGreeting):
 * same quote list, one per day of the year. Local time decides the greeting,
 * so it is filled in on the client (inline script / AuthFrame), never SSR.
 */
export const SKELETON_QUOTES: ReadonlyArray<{ text: string; name: string }> = [
  { text: "Less, but better.", name: "Dieter Rams" },
  { text: "Stay hungry, stay foolish.", name: "Steve Jobs" },
  { text: "Well done is better than well said.", name: "Benjamin Franklin" },
  { text: "Simplicity is the ultimate sophistication.", name: "Leonardo da Vinci" },
  { text: "Quality is not an act, it is a habit.", name: "Aristotle" },
  { text: "You miss 100% of the shots you don\u2019t take.", name: "Wayne Gretzky" },
  { text: "Done is better than perfect.", name: "Sheryl Sandberg" },
  { text: "The details are not the details. They make the design.", name: "Charles Eames" },
];

export type SkeletonGreeting = {
  title: string;
  quoteText: string;
  quoteName: string;
};

export function skeletonGreeting(now = new Date()): SkeletonGreeting {
  const hour = now.getHours();
  const title = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const start = new Date(now.getFullYear(), 0, 0);
  const day = Math.floor((now.getTime() - start.getTime()) / 86400000);
  const pick = SKELETON_QUOTES[day % SKELETON_QUOTES.length];
  return { title, quoteText: pick.text, quoteName: pick.name };
}

/** Same as skeletonGreeting, as inline ES5 filling `.ysbf-greet` in element f. */
function greetingFillSource(): string {
  return `var greets=f.querySelectorAll(".ysbf-greet");if(greets.length){var now=new Date(),hr=now.getHours(),Q=${JSON.stringify(
    SKELETON_QUOTES,
  )};for(var gi=0;gi<greets.length;gi++){var G=greets[gi];var gs=document.createElement("strong");gs.textContent=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";G.textContent="";G.appendChild(gs)}}`;
}

/** Length of the theme-gradient colour motion (ysbf-theme-cycle, 36s in studio-boot-frame.css). */
export const AUTH_CYCLE_MS = 36_000;
const CYCLE_MS = AUTH_CYCLE_MS;

/** Keys the shell never restores (sanitizePersistedOpenTabs / isRetiredStudioTab). */
const SKIPPED_TAB_PREFIXES = ["composer:", "thread:", "agent:", "network:", "offers:", "dashboard:"];

/**
 * Body script, placed right after the frame element: switches the frame on
 * for "app", and for "auth" as the signed-out skeleton (data-auth: no ring,
 * no saved tabs, theme-colour drift) that the sign-in page takes over. Set on the element itself (not only on <html>) because React's
 * dev remount resets <html> attributes it does not own.
 *
 * It also swaps the placeholder tabs for the saved strip when exactly one
 * account's open-tabs blob is stored (with several accounts on one browser we
 * cannot know which one is signing in yet, so the placeholders stay). Titles
 * go in via textContent only; the X icon is cloned from the server markup.
 */
export function getBootFrameRevealInlineScript(): string {
  const cfg = JSON.stringify({
    id: BOOT_FRAME_ID,
    base: OPEN_TABS_BASE + ":",
    titles: STATIC_TAB_TITLES,
    admin: ADMIN_TAB_TITLES,
    skip: SKIPPED_TAB_PREFIXES,
  });
  return `(function(){try{var B=window.__ysBoot;if(B!=="app"&&B!=="auth")return;var C=${cfg};var f=document.getElementById(C.id);if(!f)return;f.style.setProperty("--ysbf-cycle-delay",(-(Date.now()%${CYCLE_MS}))+"ms");if(B==="auth")return;f.setAttribute("data-on","");try{${greetingFillSource()}}catch(e){}var strip=f.querySelector(".ysbf-tabs");var tpl=strip&&strip.querySelector(".ysbf-tab-x");if(!tpl)return;var ls=window.localStorage,key=null,n=0;for(var i=0;i<ls.length;i++){var k=ls.key(i);if(k&&k.indexOf(C.base)===0){n++;key=k}}if(n!==1)return;var s=JSON.parse(ls.getItem(key)||"null");if(!s||!Array.isArray(s.openTabs))return;var snaps=s.snapshots&&typeof s.snapshots==="object"?s.snapshots:{};var starts=function(t,p){return t.indexOf(p)===0};var title=function(t){var sn=snaps[t];if(sn&&typeof sn.title==="string"&&sn.title.trim())return sn.title.trim();if(sn&&typeof sn.name==="string"&&sn.name.trim())return sn.name.trim();if(starts(t,"feed:"))return starts(t,"feed:following")?"Following":"For You";if(starts(t,"billing:")){var b=t.slice(8);return b==="invoices"?"Invoices":b==="topup"?"Top-up":"Plans"}if(starts(t,"admin:"))return C.admin[t.slice(6)]||"Admin";for(var p in C.titles){if(starts(t,p))return C.titles[p]}return""};var keys=[],feedAt=-1;for(var j=0;j<s.openTabs.length;j++){var t=s.openTabs[j];if(typeof t!=="string"||!t)continue;var bad=false;for(var q=0;q<C.skip.length;q++){if(starts(t,C.skip[q]))bad=true}if(bad)continue;if(starts(t,"feed:")){if(feedAt<0){feedAt=keys.length;keys.push(t)}else keys[feedAt]=t;continue}if(keys.indexOf(t)<0)keys.push(t)}keys=keys.slice(0,12);var active=keys.indexOf(s.activeTab)>=0?s.activeTab:(starts(String(s.activeTab||""),"feed:")&&feedAt>=0?keys[feedAt]:keys[keys.length-1]);var ic=strip.querySelector(".ysbf-tab-icon");strip.textContent="";for(var m=0;m<keys.length;m++){var el=document.createElement("span");el.className="ysbf-tab"+(keys[m]===active?" is-active":"");if(ic&&starts(keys[m],"messages:")){el.appendChild(ic.cloneNode(true))}else{var id=document.createElement("span");id.className="ysbf-tab-icon is-skel";el.appendChild(id)}var lab=document.createElement("span");lab.className="ysbf-tab-label";var tt=title(keys[m]);if(tt){lab.textContent=tt}else{var sk=document.createElement("span");sk.className="ysbf-tab-skel";sk.style.width=(44+(m*17)%28)+"%";lab.appendChild(sk)}el.appendChild(lab);el.appendChild(tpl.cloneNode(true));strip.appendChild(el)}if(keys.length<2){var ca=f.querySelector(".ysbf-closeall");if(ca)ca.setAttribute("hidden","")}}catch(e){}})();`;
}
